import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Sparkle, Check, X as XIcon } from '@phosphor-icons/react';
import { fetchPublicQuote, respondPublicQuote } from '../lib/api';
import { toast } from 'sonner';

const STATUS_LABEL = {
  sent_to_customer: { label: 'Awaiting your decision', color: 'bg-sky-200 text-sky-900' },
  accepted: { label: 'Accepted', color: 'bg-emerald-600 text-white' },
  declined: { label: 'Declined', color: 'bg-rose-300 text-rose-900' },
  expired: { label: 'Expired', color: 'bg-zinc-300 text-zinc-700' },
};

export default function PublicQuote() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');

  const load = async () => {
    try { const r = await fetchPublicQuote(token); setQuote(r.data); }
    catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Quote unavailable');
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const respond = async (accept) => {
    setBusy(true);
    try {
      await respondPublicQuote(token, accept, note);
      toast.success(accept ? 'Quote accepted — thank you!' : 'Quote declined');
      await load();
    } catch (e) {
      toast.error('Failed to record your response — please refresh and try again');
    } finally { setBusy(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="border border-border p-8 max-w-md text-center">
          <div className="text-rose-700 font-bold tracking-tight mb-2">Quote unavailable</div>
          <div className="text-sm text-muted-foreground">{error}</div>
        </div>
      </div>
    );
  }
  if (!quote) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin h-8 w-8 border-2 border-brand border-t-transparent rounded-full" /></div>;
  }

  const t = quote.totals || {};
  const status = STATUS_LABEL[quote.status] || { label: quote.status, color: 'bg-zinc-200' };
  const decided = ['accepted', 'declined', 'expired'].includes(quote.status);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="public-quote-page">
      <nav className="border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="text-lg font-bold tracking-tight flex items-center gap-2">
            <Sparkle size={22} weight="fill" className="text-brand" /> STOCKARIS
          </div>
          <Badge className={status.color} data-testid="public-quote-status">{status.label}</Badge>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-10">
        <p className="text-xs uppercase tracking-[0.3em] font-bold text-brand mb-2">Service Quote</p>
        <h1 className="text-3xl font-bold tracking-tight mb-1">{quote.title}</h1>
        <p className="text-sm text-muted-foreground mb-6">For {quote.customer_name || quote.customer_email}</p>

        <div className="border border-border" data-testid="public-quote-lines">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-widest">
              <tr><th className="text-left p-2">Service</th><th className="text-left p-2">Description</th><th className="text-right p-2">Qty</th><th className="text-right p-2">Unit price</th><th className="text-right p-2">Line total</th></tr>
            </thead>
            <tbody>
              {(quote.lines || []).map((l, i) => (
                <tr key={i} className="border-t border-border">
                  <td className="p-2 font-mono text-xs">{l.service_code}</td>
                  <td className="p-2">{l.label}</td>
                  <td className="p-2 text-right tabular-nums">{l.qty} {l.unit}</td>
                  <td className="p-2 text-right tabular-nums">{quote.currency} {Number(l.unit_price).toFixed(2)}</td>
                  <td className="p-2 text-right tabular-nums font-bold">{quote.currency} {(Number(l.qty) * Number(l.unit_price)).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-border bg-muted/20">
                <td colSpan="4" className="p-2 text-right text-xs uppercase tracking-widest font-bold">Total</td>
                <td className="p-2 text-right text-xl font-bold tabular-nums" data-testid="public-quote-total">{quote.currency} {(t.revenue ?? 0).toLocaleString()}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {quote.notes && (
          <div className="mt-4 border border-border p-3 text-sm bg-muted/20">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Notes</div>
            <div className="whitespace-pre-wrap">{quote.notes}</div>
          </div>
        )}

        {quote.valid_until && (
          <div className="mt-4 text-xs text-muted-foreground">Valid until {new Date(quote.valid_until).toLocaleString()}</div>
        )}

        {!decided && quote.status === 'sent_to_customer' && (
          <div className="mt-8 border border-border p-5">
            <div className="text-sm font-bold tracking-tight mb-3">Your decision</div>
            <Textarea data-testid="public-quote-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional message back to us…" />
            <div className="mt-3 flex gap-3">
              <Button data-testid="public-quote-accept" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => respond(true)} disabled={busy}>
                <Check size={16} className="mr-1" /> Accept quote
              </Button>
              <Button data-testid="public-quote-decline" variant="outline" onClick={() => respond(false)} disabled={busy}>
                <XIcon size={16} className="mr-1" /> Decline
              </Button>
            </div>
          </div>
        )}

        {decided && quote.responded_at && (
          <div className="mt-8 border border-border bg-muted/20 p-5 text-sm" data-testid="public-quote-decided">
            Decision recorded {new Date(quote.responded_at).toLocaleString()}.
            {quote.customer_note && (
              <div className="mt-2 text-muted-foreground">"{quote.customer_note}"</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
